import { watercraftApiSlice } from '../../store/apiSlices/watercraftApiSlice';
import type { StripeInterval } from '../../utils/payment-utils';

export interface WatercraftPrice {
  id: string;
  cost: number;
  original_cost: number;
  currency: string;
  interval?: StripeInterval | null;
}

export interface SubscriptionPricesResponse {
  prices?: WatercraftPrice[];
  trial_days?: number;
}

export type SubscriptionStatus =
  | 'trialing'
  | 'active'
  | 'past_due'
  | 'cancelled'
  | 'incomplete'
  | null;

export type SubscriptionSource =
  | 'stripe'
  | 'apple'
  | 'google'
  | 'granted'
  | 'complimentary'
  | null;

export interface SubscriptionStatusResponse {
  has_access: boolean;
  status?: SubscriptionStatus;
  source?: SubscriptionSource;
  is_lifetime?: boolean;
  current_period_end?: string | null;
  trial_end?: string | null;
  cancel_at_period_end?: boolean;
  expires_at?: string | null;
}

export interface CheckoutSessionRequest {
  price_id: string;
  success_url: string;
  cancel_url: string;
  request_trial?: boolean;
  coupon_id?: string | null;
}

export interface VerifyCheckoutRequest {
  checkout_session_id: string;
}

export interface BillingPortalRequest {
  return_url: string;
}

export const watercraftSubscriptionSlice = watercraftApiSlice
  .enhanceEndpoints({ addTagTypes: ['WatercraftSubscription'] })
  .injectEndpoints({
    endpoints: (builder) => ({
      getPrices: builder.query<SubscriptionPricesResponse, string | void>({
        query: (couponId) => ({
          url: 'subscription/stripe/prices',
          method: 'GET',
          params: couponId ? { coupon_id: couponId } : undefined,
          useDefaultErrorHandler: false,
        }),
      }),
      getSubscriptionStatus: builder.query<SubscriptionStatusResponse, void>({
        query: () => ({
          url: 'subscription',
          method: 'GET',
          useDefaultErrorHandler: false,
        }),
        providesTags: ['WatercraftSubscription'],
      }),
      createCheckoutSession: builder.mutation<
        string | undefined,
        CheckoutSessionRequest
      >({
        query: (request) => ({
          url: 'subscription/stripe/checkout-session',
          method: 'POST',
          data: { request_trial: true, ...request },
          useDefaultErrorHandler: false,
        }),
        transformResponse: (response: { redirect_url: string } | undefined) =>
          response?.redirect_url,
      }),
      verifyCheckout: builder.mutation<
        SubscriptionStatusResponse,
        VerifyCheckoutRequest
      >({
        query: (request) => ({
          url: 'subscription/stripe/verify',
          method: 'POST',
          data: request,
          useDefaultErrorHandler: false,
        }),
        invalidatesTags: ['WatercraftSubscription'],
      }),
      createBillingPortalSession: builder.mutation<
        string | undefined,
        BillingPortalRequest
      >({
        query: (request) => ({
          url: 'subscription/stripe/billing-portal',
          method: 'POST',
          data: request,
          useDefaultErrorHandler: false,
        }),
        transformResponse: (response: { redirect_url: string } | undefined) =>
          response?.redirect_url,
      }),
    }),
  });

export const {
  useGetPricesQuery,
  useGetSubscriptionStatusQuery,
  useLazyGetSubscriptionStatusQuery,
  useCreateCheckoutSessionMutation,
  useVerifyCheckoutMutation,
  useCreateBillingPortalSessionMutation,
} = watercraftSubscriptionSlice;
